import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { DebtorService } from '@web-frontend/shared/services/debtor/debtor.service';
import { IDebtor, IDebtorDto } from '@web-frontend/shared/interfaces/debtor';
import { ToastService } from '@web-frontend/shared/services/toast/toast.service';

@Component({
  selector: 'roma-wrapper-sepa',
  templateUrl: './wrapper-sepa.component.html',
  styleUrls: ['./wrapper-sepa.component.scss'],
})
export class WrapperSepaComponent implements OnInit, OnDestroy {
  @Input() customerId = '';
  @Input() customerName = '';

  @Output() updateFormStatus = new EventEmitter<boolean>();

  form: FormGroup;
  isLoading = false;
  isSaving = false;
  debtor: IDebtor | null = null;

  sequenceTypeOptions: Array<{ value: string; label: string }> = [];

  private sub$ = new Subscription();
  private initialFormValue: string;

  constructor(
    private fb: FormBuilder,
    private debtorService: DebtorService,
    private toastService: ToastService,
    private i18n: TranslateService
  ) {
    this.form = this.fb.group({
      iban: ['', [Validators.required]],
      bic: [''],
      mandateId: [''],
      mandateSignatureDate: [''],
      sequenceType: ['RCUR', [Validators.required]],
      usualAmount: [0, [Validators.required, Validators.min(0)]],
      collectionDay: [
        1,
        [Validators.required, Validators.min(1), Validators.max(28)],
      ],
      baseConcept: [''],
    });
  }

  ngOnInit(): void {
    this.sequenceTypeOptions = [
      {
        value: 'RCUR',
        label: 'RCUR – ' + this.i18n.instant('sepa.sequenceRecurrent'),
      },
      {
        value: 'FRST',
        label: 'FRST – ' + this.i18n.instant('sepa.sequenceFirst'),
      },
      {
        value: 'OOFF',
        label: 'OOFF – ' + this.i18n.instant('sepa.sequenceOneOff'),
      },
      {
        value: 'FNAL',
        label: 'FNAL – ' + this.i18n.instant('sepa.sequenceFinal'),
      },
    ];

    this.loadDebtor();

    this.sub$.add(
      this.form.valueChanges.subscribe(() => {
        const hasChanges =
          JSON.stringify(this.form.value) !== this.initialFormValue;
        this.updateFormStatus.emit(hasChanges);
      })
    );
  }

  ngOnDestroy(): void {
    this.sub$.unsubscribe();
  }

  private loadDebtor(): void {
    if (!this.customerId) {
      return;
    }

    this.isLoading = true;
    this.sub$.add(
      this.debtorService
        .getByClientId(this.customerId)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: (debtor) => {
            this.debtor = debtor;
            this.patchForm(debtor);
          },
          error: () => {
            this.debtor = null;
            this.captureInitialFormValue();
          },
        })
    );
  }

  private patchForm(debtor: IDebtor): void {
    const signDate = debtor.mandateSignatureDate
      ? this.timestampToDateString(debtor.mandateSignatureDate)
      : '';

    this.form.patchValue({
      iban: debtor.iban || '',
      bic: debtor.bic || '',
      mandateId: debtor.mandateId || '',
      mandateSignatureDate: signDate,
      sequenceType: debtor.sequenceType || 'RCUR',
      usualAmount: debtor.usualAmount?.amount || 0,
      collectionDay: debtor.collectionDay || 1,
      baseConcept: debtor.baseConcept || '',
    });

    this.captureInitialFormValue();
  }

  private captureInitialFormValue(): void {
    this.initialFormValue = JSON.stringify(this.form.value);
  }

  save(): void {
    if (this.form.invalid || this.isSaving) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.form.disable();

    const ensureDebtor$ = this.debtor
      ? of(this.debtor)
      : this.debtorService
          .getByClientId(this.customerId)
          .pipe(catchError(() => of(null as IDebtor)));

    this.sub$.add(
      ensureDebtor$
        .pipe(
          switchMap((existing) => {
            if (existing?.id) {
              this.debtor = existing;
            }
            const dto = this.buildDto();
            return this.debtor
              ? this.debtorService.update(this.debtor.id, dto)
              : this.debtorService.create(dto);
          }),
          finalize(() => {
            this.isSaving = false;
            this.form.enable();
          })
        )
        .subscribe({
          next: (result) => {
            this.debtor = result;
            this.patchForm(result);
            this.toastService.show({
              text: 'sepa.savedSuccess',
              type: 'success',
              msDuration: 4000,
              class: '',
            });
          },
          error: () => {
            this.toastService.show({
              text: 'sepa.savedError',
              type: 'error',
              msDuration: 4000,
              class: '',
            });
          },
        })
    );
  }

  private buildDto(): IDebtorDto {
    const v = this.form.getRawValue();
    const dto: IDebtorDto = {
      name: this.customerName,
      bankAccount: {
        iban: v.iban,
        ...(v.bic ? { bic: v.bic } : {}),
      },
      sequenceType: v.sequenceType,
      usualAmount: {
        amount: Number(v.usualAmount),
        currency: { code: 'EUR', symbol: '€' },
      },
      collectionDay: Number(v.collectionDay),
    };

    if (v.mandateId) {
      dto.mandate = {
        mandateId: v.mandateId,
        mandateSignatureDate: v.mandateSignatureDate
          ? new Date(v.mandateSignatureDate).getTime()
          : Date.now(),
      };
    }

    if (v.baseConcept) {
      dto.baseConcept = v.baseConcept;
    }

    if (!this.debtor) {
      dto.clientId = this.customerId;
    }

    return dto;
  }

  private timestampToDateString(ts: number): string {
    const d = new Date(ts);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return control?.invalid && control?.touched;
  }
}
